import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FormlyFieldConfig } from '@ngx-formly/core';

interface IHistoryEntry {
  fields: FormlyFieldConfig[];
  timestamp: number;
}

@Injectable()
export class FormBuilderV2HistoryService {
  private undoStack: IHistoryEntry[] = [];
  private redoStack: IHistoryEntry[] = [];
  private maxHistory = 50;

  // Set to true while an undo/redo result is being applied so the central
  // capture (in the canvas) skips re-recording the restored state.
  isRestoring = false;

  canUndo$ = new BehaviorSubject<boolean>(false);
  canRedo$ = new BehaviorSubject<boolean>(false);

  pushState(fields: FormlyFieldConfig[]): void {
    const serialized = JSON.stringify(fields ?? []);

    // Skip no-op snapshots (e.g. tab switches / refreshes that re-emit the
    // same fields) so they don't pollute the undo history.
    const top = this.undoStack[this.undoStack.length - 1];
    if (top && JSON.stringify(top.fields) === serialized) {
      return;
    }

    const snapshot: IHistoryEntry = {
      fields: JSON.parse(serialized),
      timestamp: Date.now(),
    };
    this.undoStack.push(snapshot);
    if (this.undoStack.length > this.maxHistory) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this.updateFlags();
  }

  undo(): FormlyFieldConfig[] | null {
    if (this.undoStack.length < 2) return null;
    const current = this.undoStack.pop()!;
    this.redoStack.push(current);
    const previous = this.undoStack[this.undoStack.length - 1];
    this.updateFlags();
    return JSON.parse(JSON.stringify(previous.fields));
  }

  redo(): FormlyFieldConfig[] | null {
    if (this.redoStack.length === 0) return null;
    const next = this.redoStack.pop()!;
    this.undoStack.push(next);
    this.updateFlags();
    return JSON.parse(JSON.stringify(next.fields));
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.updateFlags();
  }

  // Collapse all snapshots into a single baseline (used after the builder has
  // finished loading/normalising so "Undo" stays disabled until a real edit).
  resetToBaseline(fields: FormlyFieldConfig[]): void {
    this.undoStack = [
      {
        fields: JSON.parse(JSON.stringify(fields ?? [])),
        timestamp: Date.now(),
      },
    ];
    this.redoStack = [];
    this.updateFlags();
  }

  private updateFlags(): void {
    this.canUndo$.next(this.undoStack.length > 1);
    this.canRedo$.next(this.redoStack.length > 0);
  }
}
